import * as React from 'react';
import { ActionAfterViewed } from '@cian/action-after-viewed-component';
import { getLogger } from '@cian/logger/browser';
import { getTelemetry } from '@cian/telemetry/browser';
import { useSelector } from 'react-redux';

import { IRoutingBuildSuccessParams, Map as MapComponent } from '../../components/Map';
import { InfrastructureMapTypes, MapRouteButton, MapRouteOnboarding } from '../../components/Map/parts';
import { YANDEX_API_TELEMETRY_KEY } from '../../constants/map';
import { getMapApiKey, getMapApiVersion } from '../../selectors/common';
import { getIsDistanceCalculatingEnabled } from '../../selectors/distanceCalculating';
import { getGeoCoordinates } from '../../selectors/newbuilding';
import { EPanoramaState, IInfrastructureItem } from '../../types/map';
import { NearbyNewbuildingsLinkContainer } from '../NearbyNewbuildingsLink';
import { SpecialPromoLinkContainer } from '../SpecialPromoLink';
import {
  trackBuildRouteClick,
  trackBuildRouteClose,
  trackBuildRouteSuccess,
  trackFullscreenShow,
  trackInfrastructureClick,
  trackInfrastructureShow,
  trackPanoramaShow,
  trackRulerClick,
} from './analytics';
import { checkIsInfrastructureRoute, getZoom } from './helpers';
import { useGeoObjectBalloon, useGeoObjects, useMapControls, useMapOnboardingState } from './hooks';

export function MapContainer() {
  const pinCoordinates = useSelector(getGeoCoordinates);
  const apiKey = useSelector(getMapApiKey);
  const apiVersion = useSelector(getMapApiVersion);
  const isDistanceCalculatingEnabled = useSelector(getIsDistanceCalculatingEnabled);

  const currentRouteItemRef = React.useRef<IInfrastructureItem | null>(null);
  const infrastructureTypeRef = React.useRef<string | undefined>();

  const { infrastructureItemsRef, infrastructureTypes, onGeoObjectsFirstFetch } = useGeoObjects();
  const [mapRef, setRoutingStart] = useMapControls();
  const [isOnboardingOpen, closeOnboarding] = useMapOnboardingState();

  const [currentTypes, setCurrentTypes] = React.useState(['main']);
  const [isFullscreen, setFullscreenState] = React.useState(false);
  const [isMapLoaded, setIsMapLoaded] = React.useState(false);
  const [isRouteMode, setRouteMode] = React.useState(false);
  const [panoramaState, setPanoramaState] = React.useState<EPanoramaState | null>(null);

  const handleRoutingClick = React.useCallback(() => {
    closeOnboarding();
    setRoutingStart();
    trackBuildRouteClick(infrastructureTypeRef.current);
  }, [closeOnboarding, setRoutingStart]);

  const handleRouteBuildingSuccess = React.useCallback((params: IRoutingBuildSuccessParams) => {
    if (params.routes.length) {
      const infrastructurePoint = currentRouteItemRef.current;
      const isObjectPoint = !!infrastructurePoint && checkIsInfrastructureRoute(infrastructurePoint, params);

      if (isObjectPoint) {
        currentRouteItemRef.current = null;
      }

      trackBuildRouteSuccess({
        searchResult: params,
        objectType: infrastructureTypeRef.current,
        isObjectPoint,
      });
    }
  }, []);

  const handleRoutingBuildInit = React.useCallback(() => {
    getTelemetry().increment(YANDEX_API_TELEMETRY_KEY, 2);
  }, []);

  const handleRouteButtonClick = React.useCallback(
    (item: IInfrastructureItem) => {
      currentRouteItemRef.current = item;
      closeOnboarding();
      setRoutingStart(item);
      trackBuildRouteClick(infrastructureTypeRef.current);
    },
    [closeOnboarding, setRoutingStart],
  );

  const { onBalloonHtmlFirstFetch, onBalloonOpen } = useGeoObjectBalloon({
    infrastructureItemsRef,
    onRouteButtonClick: handleRouteButtonClick,
  });

  const setInfrastructureType = React.useCallback((types: string[]) => {
    infrastructureTypeRef.current = types.find(type => type !== 'main');
    setCurrentTypes(types);
    trackInfrastructureClick(infrastructureTypeRef.current);
  }, []);

  const handleFullscreenEnter = React.useCallback(() => {
    setFullscreenState(true);
    trackFullscreenShow(true, infrastructureTypeRef.current);
  }, []);

  const handleFullscreenExit = React.useCallback(() => {
    setFullscreenState(false);
    trackFullscreenShow(false, infrastructureTypeRef.current);
  }, []);

  const handleMapLoaded = React.useCallback(() => setIsMapLoaded(true), []);
  const handleRouteModeEnter = React.useCallback(() => setRouteMode(true), []);

  const handleRouteModeExit = React.useCallback(() => {
    setRouteMode(false);
    trackBuildRouteClose();
  }, []);

  const handlePanoramaSuccessEnter = React.useCallback(() => {
    getTelemetry().increment(YANDEX_API_TELEMETRY_KEY, 5);
    setPanoramaState(EPanoramaState.Loaded);
    trackPanoramaShow(true, infrastructureTypeRef.current);
  }, []);

  const handleNoPanoramaEnter = React.useCallback(() => {
    getTelemetry().increment(YANDEX_API_TELEMETRY_KEY, 5);
    setPanoramaState(EPanoramaState.None);
    trackPanoramaShow(true, infrastructureTypeRef.current);
  }, []);

  const handlePanoramaExit = React.useCallback(() => {
    setPanoramaState(null);
    trackPanoramaShow(false, infrastructureTypeRef.current);
  }, []);

  const handleRulerClick = React.useCallback(() => {
    trackRulerClick(infrastructureTypeRef.current);
  }, []);

  const handleError = React.useCallback((error: Error) => {
    getLogger().error(error);
  }, []);

  if (!pinCoordinates) {
    return null;
  }

  const isPanoramaMode = panoramaState !== null;
  const isInfrastructureMode = !isPanoramaMode && !isRouteMode;
  const isRouteButtonVisible = isMapLoaded && isInfrastructureMode && isDistanceCalculatingEnabled;

  return (
    <ActionAfterViewed callback={trackInfrastructureShow} percentVisible={0.5}>
      <MapComponent
        mapRef={mapRef}
        isFullscreen={isFullscreen}
        pinCoordinates={pinCoordinates}
        currentTypes={currentTypes}
        zoom={getZoom(currentTypes.length)}
        onGeoObjectsFirstFetch={onGeoObjectsFirstFetch}
        onBalloonHtmlFirstFetch={onBalloonHtmlFirstFetch}
        infrastructureTypes={
          isInfrastructureMode &&
          !!infrastructureTypes && (
            <InfrastructureMapTypes
              types={infrastructureTypes}
              currentTypes={currentTypes}
              onTypesChange={setInfrastructureType}
            />
          )
        }
        specialPromo={<SpecialPromoLinkContainer />}
        nearbyNewbuildingsLink={<NearbyNewbuildingsLinkContainer />}
        routeButton={
          isRouteButtonVisible ? (
            <MapRouteOnboarding open={isOnboardingOpen} onClose={closeOnboarding}>
              <MapRouteButton onClick={handleRoutingClick} />
            </MapRouteOnboarding>
          ) : null
        }
        apiKey={apiKey}
        apiVersion={apiVersion}
        onError={handleError}
        onFullscreenEnter={handleFullscreenEnter}
        onFullscreenExit={handleFullscreenExit}
        onPanoramaSuccessEnter={handlePanoramaSuccessEnter}
        onNoPanoramaEnter={handleNoPanoramaEnter}
        onPanoramaExit={handlePanoramaExit}
        onMapLoaded={handleMapLoaded}
        onBalloonOpen={onBalloonOpen}
        onRoutingStart={handleRouteModeEnter}
        onRoutingEnd={handleRouteModeExit}
        onRouteBuildingInit={handleRoutingBuildInit}
        onRouteBuildingSuccess={handleRouteBuildingSuccess}
        onRulerClick={handleRulerClick}
      />
    </ActionAfterViewed>
  );
}
